import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Language } from '../../../constants/language';
import { LoadingSpinner, ProductListItem, ProductDetailsModal } from '../../ui';
import { ProductCard } from '../../sections/store/products';
import { Product, ProductType } from '../../../models/product/product';
import { productService } from '../../../services/product-service';
import { Filter, FilterViewMode, SortOption } from '../../../models/filters/filters';
import { Cart, CartItem } from '../../../models/cart/cart';
import {PAGINATION} from "../../../utils/pagination-util";
import {CartSidebar} from "../../sections/store";
import { StorageUtil } from '../../../utils/storage-util';
import { usePageSEO } from '../../../hooks';

const Store: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the store page
  usePageSEO('store');

  const { t, i18n } = useTranslation();

  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isRTL = i18n.language === Language.AR;
  const currentLanguage = i18n.language as Language;

  // State management with persistence
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<FilterViewMode>(() => 
    StorageUtil.getConfig(StorageUtil.KEYS.VIEW_MODE, FilterViewMode.LIST)
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<Cart>(() => 
    StorageUtil.getConfig(StorageUtil.KEYS.CART_ITEMS, { items: [], total: 0, itemCount: 0 })
  );
  const [showCart, setShowCart] = useState(false);
  const [currentPage, setCurrentPage] = useState(PAGINATION.DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(PAGINATION.DEFAULT_PAGE);
  const [totalProducts, setTotalProducts] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => 
    StorageUtil.getConfig(StorageUtil.KEYS.ITEMS_PER_PAGE, PAGINATION.DEFAULT_PAGE_SIZE)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [productType, setProductType] = useState<ProductType>(ProductType.ALL);

  const [filters, setFilters] = useState<Filter>(() => 
    StorageUtil.getConfig(StorageUtil.KEYS.FILTERS, {})
  );

  const [sortBy, setSortBy] = useState<SortOption>(() => 
    StorageUtil.getConfig(StorageUtil.KEYS.SORT_PREFERENCES, {
      field: 'title',
      direction: 'asc'
    })
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productResponse] = await Promise.all([
          productService.getProducts(currentLanguage,
                                    productType,
                                    filters,
                                    sortBy,
                                    currentPage,
                                    itemsPerPage)
        ]);
        
        setProducts(productResponse.data);
        setTotalPages(productResponse.totalPages);
        setTotalProducts(productResponse.total);
      } catch (error) {
          console.error('Error fetching products:', error);
      } finally {
          setLoading(false);
      }
    };

    fetchData();
  }, [currentLanguage, filters, sortBy, currentPage, itemsPerPage, productType]);

  // Handle product selection from URL (both path params and query params)
  useEffect(() => {
    const selectProductFromUrl = async () => {
      // Check path parameter first, then query parameter
      const productIdFromUrl = productId || searchParams.get('product');
      
      if (productIdFromUrl && !selectedProduct) {
        try {
          const product = await productService.getProductById(parseInt(productIdFromUrl), currentLanguage);
          if (product) {
            setSelectedProduct(product);
            setProductType(product.type);
            
            // If product came from query parameter, clean up the URL by redirecting to clean path
            if (searchParams.get('product')) {
              navigate(`/store/product/${productIdFromUrl}`, { replace: true });
            }
          }
        } catch (error) {
          console.error('Error loading product from URL:', error);
          // If product not found, redirect to store without the product parameter
          if (searchParams.get('product')) {
            navigate('/store', { replace: true });
          }
        }
      }
    };

    selectProductFromUrl();
  }, [productId, searchParams, selectedProduct, currentLanguage, navigate]);

  // Search functionality
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim()) {
        setLoading(true);
        try {
          const results = await productService.searchProducts(searchQuery, currentLanguage, productType, filters, sortBy);
          setProducts(results);
          setTotalPages(1); // Search results in single page
        } catch (error) {
          console.error('Error searching products:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, currentLanguage, filters, sortBy, productType]);

  // Persistence hooks - save user preferences to storage
  useEffect(() => {
    StorageUtil.setConfig(StorageUtil.KEYS.VIEW_MODE, viewMode);
  }, [viewMode]);

  useEffect(() => {
    StorageUtil.setConfig(StorageUtil.KEYS.ITEMS_PER_PAGE, itemsPerPage);
  }, [itemsPerPage]);

  useEffect(() => {
    StorageUtil.setConfig(StorageUtil.KEYS.FILTERS, filters);
  }, [filters]);

  useEffect(() => {
    StorageUtil.setConfig(StorageUtil.KEYS.SORT_PREFERENCES, sortBy);
  }, [sortBy]);

  useEffect(() => {
    StorageUtil.setConfig(StorageUtil.KEYS.CART_ITEMS, cart);
  }, [cart]);

  // Cart functions
  const addToCart = async (product: Product) => {
    const cartKey = `${product.id}-${product.type}`;
    
    // Set loading state
    setAddingToCart(prev => new Set([...prev, cartKey]));
    
    try {
      // Simulate API call delay for better UX
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const existingItem = cart.items.find(item => 
        item.product.id === product.id
      );

      let newItems: CartItem[];
      if (existingItem) {
        newItems = cart.items.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newItems = [...cart.items, { product, quantity: 1 }];
      }

      const newTotal = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      setCart({ items: newItems, total: newTotal, itemCount: newItemCount });
      
      // Auto-show cart sidebar after successful add
      setShowCart(true);
    } finally {
      // Remove loading state
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartKey);
        return newSet;
      });
    }
  };

  const removeFromCart = (bookId: number) => {
    const newItems = cart.items.filter(item => 
      item.product.id !== bookId
    );

    const newTotal = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

    setCart({ items: newItems, total: newTotal, itemCount: newItemCount });
  };

  const updateCartQuantity = (bookId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(bookId);
      return;
    }

    const newItems = cart.items.map(item =>
      item.product.id === bookId
        ? { ...item, quantity }
        : item
    );

    const newTotal = newItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

    setCart({ items: newItems, total: newTotal, itemCount: newItemCount });
  };

  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header Section */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              {t('pages.store.title', 'Christian E-Books')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('pages.store.subtitle', 'Discover transformative Christian literature that will deepen your faith and inspire your journey')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center justify-between w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-left hover:bg-gray-50 transition-colors touch-manipulation"
            >
              <span className="font-medium text-gray-900">
                {t('filter.title', 'Filters')}
              </span>
              <svg 
                className={`w-5 h-5 text-gray-500 transform transition-transform ${showMobileFilters ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Sidebar */}
          <div className={`w-full lg:w-80 lg:flex-shrink-0 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 lg:sticky lg:top-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {/* Search Bar */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  {t('search.title', 'Search Books')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('search.placeholder', 'Search books, authors, or topics...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent ${
                      isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'
                    }`}
                  />
                  <svg 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${
                      isRTL ? 'right-3' : 'left-3'
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* View Mode */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  {t('view.title', 'View Mode')}
                </label>
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode(FilterViewMode.GRID)}
                    className={`flex-1 p-2 rounded-md transition-colors flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 ${
                      viewMode === 'grid' 
                        ? 'bg-white text-black shadow-sm' 
                        : 'text-gray-600 hover:text-black'
                    }`}
                    title={t('view.grid', 'Grid View')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    <span className="text-sm">{t('view.grid', 'Grid')}</span>
                  </button>
                  <button
                    onClick={() => setViewMode(FilterViewMode.LIST)}
                    className={`flex-1 p-2 rounded-md transition-colors flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 ${
                      viewMode === 'list' 
                        ? 'bg-white text-black shadow-sm' 
                        : 'text-gray-600 hover:text-black'
                    }`}
                    title={t('view.list', 'List View')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    <span className="text-sm">{t('view.list', 'List')}</span>
                  </button>
                </div>
              </div>

              {/* Sort Options */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  {t('sort.title', 'Sort By')}
                </label>
                <select
                  value={`${sortBy.field}-${sortBy.direction}`}
                  onChange={(e) => {
                    const [field, direction] = e.target.value.split('-');
                    handleSortChange({ field: field as any, direction: direction as 'asc' | 'desc' });
                  }}
                  className={`w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white appearance-none ${
                    isRTL ? 'pl-8 pr-3' : 'pr-8 pl-3'
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="title-asc">{t('sort.title-asc', 'Title A-Z')}</option>
                  <option value="title-desc">{t('sort.title-desc', 'Title Z-A')}</option>
                  <option value="price-asc">{t('sort.price-asc', 'Price Low-High')}</option>
                  <option value="price-desc">{t('sort.price-desc', 'Price High-Low')}</option>
                  <option value="rating-desc">{t('sort.rating-desc', 'Highest Rated')}</option>
                  <option value="publishedDate-desc">{t('sort.newest', 'Newest First')}</option>
                </select>
              </div>

              {/* Filters Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  {t('filter.title', 'Filters')}
                </h3>
                
                {/* Categories Filter - Temporarily Hidden */}
                {/* 
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t('filter.categories', 'Categories')}
                  </h4>
                  <div className="space-y-2">
                    {Object.values(BookCategory).map(category => (
                      <label key={category} className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 cursor-pointer`}>
                        <input
                          type="checkbox"
                          checked={filters.category?.includes(category) || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters(prev => ({
                                ...prev,
                                category: [...(prev.category || []), category]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                category: prev.category?.filter(c => c !== category)
                              }));
                            }
                            setCurrentPage(1);
                          }}
                          className="w-4 h-4 text-black border-2 border-gray-300 rounded focus:ring-black"
                        />
                        <span className="text-sm text-gray-700">
                          {t(`products.categories.${category}`, category.replace('_', ' '))}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                */}

                {/* Price Range Filter */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t('filter.price-range', 'Price Range')}
                  </h4>
                  <div className="space-y-2">
                    <label className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 cursor-pointer`}>
                      <input
                        type="radio"
                        name="priceRange"
                        checked={!filters.priceRange}
                        onChange={() => {
                          setFilters(prev => ({ ...prev, priceRange: undefined }));
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-black border-2 border-gray-300 focus:ring-black"
                      />
                      <span className="text-sm text-gray-700">
                        {t('common.all-prices', 'All Prices')}
                      </span>
                    </label>
                    <label className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 cursor-pointer`}>
                      <input
                        type="radio"
                        name="priceRange"
                        checked={filters.priceRange?.min === 0 && filters.priceRange?.max === 1000}
                        onChange={() => {
                          setFilters(prev => ({ ...prev, priceRange: { min: 0, max: 1000 } }));
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-black border-2 border-gray-300 focus:ring-black"
                      />
                      <span className="text-sm text-gray-700">
                        {t('filter.under-1000', 'Under 1000 EGP')}
                      </span>
                    </label>
                    <label className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 cursor-pointer`}>
                      <input
                        type="radio"
                        name="priceRange"
                        checked={filters.priceRange?.min === 1000 && filters.priceRange?.max === 2000}
                        onChange={() => {
                          setFilters(prev => ({ ...prev, priceRange: { min: 1000, max: 2000 } }));
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-black border-2 border-gray-300 focus:ring-black"
                      />
                      <span className="text-sm text-gray-700">
                        {t('filter.1000-2000', '1000 - 2000 EGP')}
                      </span>
                    </label>
                    <label className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 cursor-pointer`}>
                      <input
                        type="radio"
                        name="priceRange"
                        checked={filters.priceRange?.min === 2000 && filters.priceRange?.max === 999999}
                        onChange={() => {
                          setFilters(prev => ({ ...prev, priceRange: { min: 2000, max: 999999 } }));
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-black border-2 border-gray-300 focus:ring-black"
                      />
                      <span className="text-sm text-gray-700">
                        {t('filter.over-2000', 'Over 2000 EGP')}
                      </span>
                    </label>
                  </div>
                </div>

              {/* Items Per Page */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  {t('pagination.itemsPerPage', 'Items Per Page')}
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white appearance-none ${
                    isRTL ? 'pl-8 pr-3' : 'pr-8 pl-3'
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value={5}>5 {t('pagination.itemsPerPage', 'products')}</option>
                  <option value={10}>10 {t('pagination.itemsPerPage', 'products')}</option>
                  <option value={15}>15 {t('pagination.itemsPerPage', 'products')}</option>
                  <option value={20}>20 {t('pagination.itemsPerPage', 'products')}</option>
                  <option value={25}>25 {t('pagination.itemsPerPage', 'products')}</option>
                </select>
              </div>

              {/* Clear Filters */}
              {Object.keys(filters).length > 0 && (
                <button
                  onClick={resetFilters}
                  className="w-full px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                >
                  {t('filter.clear', 'Clear All Filters')}
                </button>
              )}
            </div>              {/* Cart Button */}
              <button
                onClick={() => setShowCart(true)}
                className={`w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-3 shadow-lg relative`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                </svg>
                <span className="font-semibold">{t('cart.view', 'View Cart')}</span>
                {cart.itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                    {cart.itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Results Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                  <div className={`flex items-center justify-between ${isRTL ? 'space-x-reverse' : ''}`}>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {searchQuery ? 
                          t('search.results.search-results', 'Search Results for "{{query}}"', { query: searchQuery }) :
                          t('search.all-items', 'All Products')
                        }
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('search.results.showing', 'Showing {{count}} books', { count: products.length })}
                      </p>
                    </div>
                    
                    {/* Active Filters Display */}
                    {Object.keys(filters).length > 0 && (
                      <div className={`flex flex-wrap gap-2 items-center ${isRTL ? 'space-x-reverse' : ''}`}>
                        <span className="text-xs text-gray-500 font-medium">{t('filter.active', 'Active filters:')}</span>
                        {/* {filters.category && filters.category.map(cat => (
                          <span key={cat} className="px-2 py-1 bg-black text-white text-xs rounded-full">
                            {t(`products.categories.${cat}`, cat.replace('_', ' '))}
                          </span>
                        ))} */}
                        {filters.priceRange && (
                          <span className="px-2 py-1 bg-black text-white text-xs rounded-full">
                            {filters.priceRange.max === 1000 ? t('filter.under-1000', 'Under 1000 EGP') :
                             filters.priceRange.max === 2000 ? t('filter.1000-2000', '1000-2000 EGP') :
                             t('filter.over-2000', 'Over 2000 EGP')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Results Header */}
                                {/* Results Header */}
                <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className="flex flex-col gap-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {t('pages.store.title', 'Christian E-Books')}
                    </h2>
                    <div className="text-sm text-gray-600">
                      {loading ? (
                        <span>{t('products.loading', 'Loading books...')}</span>
                      ) : (
                        <span>
                          {t('pagination.showing', 'Showing {{start}} - {{end}} of {{total}} results', {
                            start: Math.min((currentPage - 1) * itemsPerPage + 1, totalProducts),
                            end: Math.min(currentPage * itemsPerPage, totalProducts),
                            total: totalProducts
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* View Cart Button */}
                  <button
                    onClick={() => setShowCart(true)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors touch-manipulation min-h-[44px]"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                    </svg>
                    <span className="hidden sm:inline">{t('cart.view', 'View Cart')}</span>
                    <span className="sm:hidden">{t('cart.cart', 'Cart')}</span>
                    {cart.itemCount > 0 && (
                      <span className="bg-white text-black text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1">
                        {cart.itemCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Products Display */}
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    {products.map(product => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        showAddToCart={true}
                        showQuickView={true}
                        onViewDetails={() => setSelectedProduct(product)}
                        onAddToCart={() => addToCart(product)}
                        isAddingToCart={addingToCart.has(`${product.id}-${product.type}`)}
                        isRTL={isRTL}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                    {products.map(product => (
                      <ProductListItem
                        key={product.id}
                        product={product}
                        onViewDetails={() => setSelectedProduct(product)}
                        onAddToCart={() => addToCart(product)}
                        isRTL={isRTL}
                        isAddingToCart={addingToCart.has(`${product.id}-${product.type}`)}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8">
                    <div className="flex justify-center items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      >
                        {isRTL ? '→' : '←'} {t('pagination.previous', 'Previous')}
                      </button>
                    
                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-10 h-10 rounded-lg transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-black text-white'
                                  : 'bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        {totalPages > 5 && (
                          <>
                            <span className="px-2 text-gray-500">...</span>
                            <button
                              onClick={() => setCurrentPage(totalPages)}
                              className={`w-10 h-10 rounded-lg transition-colors ${
                                currentPage === totalPages
                                  ? 'bg-black text-white'
                                  : 'bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                    
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      >
                        {t('pagination.next', 'Next')} {isRTL ? '←' : '→'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {products.length === 0 && !loading && (
                  <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t('products.no-products', 'No products found matching your criteria.')}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {t('products.no-products-suggestion', 'Try adjusting your search or filters to find what you\'re looking for.')}
                    </p>
                    {Object.keys(filters).length > 0 && (
                      <button
                        onClick={resetFilters}
                        className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        {t('filter.clear', 'Clear All Filters')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      {selectedProduct && (
        <ProductDetailsModal
          product={selectedProduct}
          productType={selectedProduct.type}
          isOpen={!!selectedProduct}
          onClose={() => {
            setSelectedProduct(null);
            // Navigate back to clean store URL regardless of how we got here
            navigate('/store', { replace: true });
          }}
          onAddToCart={() => addToCart(selectedProduct)}
          isRTL={isRTL}
          isAddingToCart={() => addingToCart.has(`${selectedProduct.id}-${selectedProduct.type}`)}
        />
      )}

      {/* Cart Sidebar */}
      <CartSidebar
        cart={cart}
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeFromCart}
        isRTL={isRTL}
      />
    </div>
  );
};

export default Store;